import { Component, Prop, h, State, Watch, Element } from '@stencil/core';

export interface SlideData {
  url: string;
  type: 'image' | 'video';
}

@Component({
  tag: 'dda-slider',
  styleUrl: 'dda-slider.css',
  shadow: true
})
export class DDASlider {
  @Element() el: HTMLElement;
  @Prop() slides: SlideData[] = [];
  @Prop() interval: number = 5000;
  @Prop() autoplay: boolean = true;
  @State() currentSlide: number = 0;
  @State() isPaused: boolean = false;
  
  private slideInterval: number;
  private videoElements: HTMLVideoElement[] = [];
  
  private playIcon = `<svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" class="play-icon">
    <polygon points="10,8 16,12 10,16" fill="white" stroke="white" stroke-width="1"/>
  </svg>`;
  
  private pauseIcon = `<svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" class="pause-icon">
    <rect x="9" y="8" width="2" height="8" fill="white" stroke="white" stroke-width="0"/>
    <rect x="13" y="8" width="2" height="8" fill="white" stroke="white" stroke-width="0"/>
  </svg>`;
  
  private svgIndicators = {
    inactive: `<svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
      <circle cx="12" cy="12" r="10" fill="#ccc" stroke="#ccc" stroke-width="2"/>
    </svg>`,
    active: (isPaused: boolean, isVideo: boolean) => `<svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
      <circle cx="12" cy="12" r="10" fill="#ccc" stroke="#ccc" stroke-width="4"/>
      ${!isVideo ? `<circle cx="12" cy="12" r="10" class="dda-slider-timer-circle" fill="none" stroke="white" stroke-width="2" 
              stroke-dasharray="63" stroke-dashoffset="63" transform="rotate(-90 12 12)"/>` : ''}
      ${isPaused ? this.playIcon : this.pauseIcon}
    </svg>`
  }; 
  
  componentDidLoad() {
    setTimeout(() => {
      this.slides.forEach((slide, index) => {
        if (slide.type === 'video') {
          const videoEl = this.el.shadowRoot.querySelector(`.dda-slide[data-index="${index}"] video`) as HTMLVideoElement;
          if (videoEl) {
            this.videoElements[index] = videoEl;
            
            videoEl.addEventListener('ended', () => {
              if (this.currentSlide === index) {
                this.nextSlide();
              }
            });
          }
        }
      });
      
      if (this.slides[this.currentSlide]?.type === 'video') {
        const currentVideo = this.videoElements[this.currentSlide];
        if (currentVideo && !this.isPaused) {
          currentVideo.play()
            .catch(error => console.warn('Initial video play failed, may need user interaction:', error));
        }
      }
      
      if (this.autoplay && !this.isPaused) {
        this.startTimer();
      }
    }, 100);
  }

  disconnectedCallback() {
    this.resetTimer();
    
    this.videoElements.forEach(video => {
      if (video) {
        video.removeEventListener('ended', this.nextSlide);
      }
    });
  }
  
  @Watch('currentSlide')
  watchCurrentSlide() {
    this.handleVideoPlayback();
    
    const currentSlideType = this.slides[this.currentSlide]?.type || 'image';
    if (this.autoplay && !this.isPaused && currentSlideType === 'image') {
      setTimeout(() => {
        this.resetTimer();
        this.startTimer();
      }, 50);
    }
  }
  
  private handleVideoPlayback() {
    this.videoElements.forEach(videoEl => {
      if (videoEl) {
        videoEl.pause();
      }
    });
    
    const currentSlideType = this.slides[this.currentSlide]?.type;
    if (currentSlideType === 'video' && !this.isPaused) {
      const currentVideo = this.videoElements[this.currentSlide];
      if (currentVideo) {
        if (currentVideo.readyState >= 2) {
          currentVideo.currentTime = 0;
          const playPromise = currentVideo.play();
          if (playPromise !== undefined) {
            playPromise.catch(error => {
              console.error('Error playing video:', error);
              this.createPlayButtonOverlay(currentVideo);
            });
          }
        } else {
          currentVideo.addEventListener('canplay', () => {
            if (this.currentSlide === this.slides.indexOf(this.slides[this.currentSlide]) && !this.isPaused) {
              currentVideo.play().catch(error => console.error('Error playing video on canplay:', error));
            }
          }, { once: true });
        }
      }
    }
  }
  
  private createPlayButtonOverlay(videoElement: HTMLVideoElement) {
    const overlay = document.createElement('div');
    overlay.className = 'dda-video-play-overlay';
    overlay.innerHTML = `
      <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" width="48" height="48">
        <circle cx="12" cy="12" r="10" fill="rgba(0,0,0,0.5)" />
        <polygon points="10,8 16,12 10,16" fill="white" />
      </svg>
    `;
    
    const videoContainer = videoElement.parentElement;
    if (videoContainer) {
      videoContainer.style.position = 'relative';
      videoContainer.appendChild(overlay);
      
      overlay.addEventListener('click', () => {
        videoElement.play()
          .then(() => {
            overlay.remove();
          })
          .catch(error => console.error('Play from overlay failed:', error));
      });
    }
  }
  
  private startTimer() {
    if (this.isPaused) return;
    this.resetTimer();
    
    const currentSlideType = this.slides[this.currentSlide]?.type;
    if (currentSlideType === 'video') return;
    
    requestAnimationFrame(() => {
      const timerCircle = this.el.shadowRoot.querySelector('.dda-slider-indicator.active .dda-slider-timer-circle') as SVGCircleElement;
      
      if (timerCircle) {
        timerCircle.style.transition = 'none';
        timerCircle.style.strokeDashoffset = '63';
        
        void timerCircle.getBoundingClientRect();
        
        setTimeout(() => {
          if (!this.isPaused) {
            timerCircle.style.transition = `stroke-dashoffset ${this.interval}ms linear`;
            timerCircle.style.strokeDashoffset = '0';
          }
        }, 20);
      }
    });
    
    this.slideInterval = window.setInterval(() => {
      if (!this.isPaused) {
        this.nextSlide();
      }
    }, this.interval);
  }
  
  private resetTimer() {
    if (this.slideInterval) {
      window.clearInterval(this.slideInterval);
      this.slideInterval = null;
    }
    
    const timerCircles = this.el.shadowRoot.querySelectorAll('.dda-slider-timer-circle');
    timerCircles.forEach(circle => {
      (circle as SVGCircleElement).style.transition = 'none';
      (circle as SVGCircleElement).style.strokeDashoffset = '63';
    });
  }
  
  private goToSlide(index: number) {
    this.currentSlide = index;
    if (this.currentSlide < 0) {
      this.currentSlide = this.slides.length - 1;
    } else if (this.currentSlide >= this.slides.length) {
      this.currentSlide = 0;
    }
  }
  
  private prevSlide = () => {
    this.goToSlide(this.currentSlide - 1);
  }
  
  private nextSlide = () => {
    this.goToSlide(this.currentSlide + 1);
  }
  
  private handleIndicatorClick = (index: number) => {
    this.goToSlide(index);
  }
  
  private togglePlayPause = (event: Event) => {
    event.stopPropagation();
    
    this.isPaused = !this.isPaused;
    
    const currentSlideType = this.slides[this.currentSlide]?.type;
    if (currentSlideType === 'video') {
      const currentVideo = this.videoElements[this.currentSlide];
      if (currentVideo) {
        if (this.isPaused) {
          currentVideo.pause();
        } else {
          const playPromise = currentVideo.play();
          if (playPromise !== undefined) {
            playPromise.catch(error => {
              console.error('Error toggling video play:', error);
              this.isPaused = true;
              this.createPlayButtonOverlay(currentVideo);
            });
          }
        }
      }
    }
    
    if (this.isPaused) {
      this.resetTimer();
      
      const activeIndicator = this.el.shadowRoot.querySelector('.dda-slider-indicator.active');
      if (activeIndicator) {
        const svgContainer = activeIndicator.querySelector('div');
        if (svgContainer) {
          svgContainer.innerHTML = this.svgIndicators.active(this.isPaused, currentSlideType === 'video');
        }
      }
    } else {
      const activeIndicator = this.el.shadowRoot.querySelector('.dda-slider-indicator.active');
      if (activeIndicator) {
        const svgContainer = activeIndicator.querySelector('div');
        if (svgContainer) {
          svgContainer.innerHTML = this.svgIndicators.active(this.isPaused, currentSlideType === 'video');
        }
      }
      
      if (currentSlideType === 'image') {
        this.startTimer();
      }
    }
  }
  
  render() {
    return (
      <div class="dda-slider-container">
        <div class="dda-slider">
          {this.slides.map((slide, index) => (
            <div class={`dda-slide ${index === this.currentSlide ? 'active' : ''}`} data-index={index}>
              {slide.type === 'image' ? (
                <img src={slide.url} alt={`Slide ${index + 1}`} />
              ) : (
                <video 
                  src={slide.url} 
                  controls={false}
                  playsInline={true}
                  preload="auto"
                  muted={false}
                />
              )}
            </div>
          ))}
        </div>
        
        <div class="dda-slider-controls-container">
          <button class="dda-slider-prev" onClick={this.prevSlide}>
            &#10094;
          </button>
          
          <div class="dda-slider-indicator-container">
            {this.slides.map((slide, index) => (
              <div 
                class={`dda-slider-indicator ${index === this.currentSlide ? 'active' : ''}`}
                onClick={() => this.handleIndicatorClick(index)}
              >
                <div innerHTML={index === this.currentSlide ? 
                  this.svgIndicators.active(this.isPaused, slide.type === 'video') : 
                  this.svgIndicators.inactive}>
                </div>
                {index === this.currentSlide && 
                  <div class="dda-slider-play-pause-overlay" onClick={this.togglePlayPause}></div>
                }
              </div>
            ))}
          </div>
          
          <button class="dda-slider-next" onClick={this.nextSlide}>
            &#10095;
          </button>
        </div>
      </div>
    );
  }
}